<template>
  <v-container fluid>
    <!-- Header -->
    <v-row class="mb-4" align="center">
      <v-col cols="12" md="3">
        <v-checkbox
          v-model="galleryView"
          label="Gallery view"
          dense
          hide-details
        />
        <v-checkbox
          v-if="galleryView"
          v-model="groupByDate"
          label="Group by date"
          dense
          hide-details
        />
      </v-col>
      <v-col cols="12" md="6">
        <h2 class="font-weight-bold">Outernet Submissions</h2>
        <p class="grey--text text--darken-1 mb-0">
          Monitor user uploads, detect duplicates, and review rewards
        </p>
      </v-col>
      <v-col cols="12" md="6" class="text-right">
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Search by User ID or Image"
          dense
          outlined
          hide-details
        />
      </v-col>
    </v-row>
    <v-col>
      <v-btn
        color="red lighten-2"
        class="mr-3"
        @click="goToQualityCheck"
        :disabled="loading"
        outlined
        large
      >
        <v-icon left>mdi-refresh</v-icon>
        Quality Check
      </v-btn>
      <v-btn
        color="indigo"
        @click="goToHeatmap"
        :disabled="loading"
        outlined
        large
      >
        <v-icon left>mdi-map-marker-radius-outline</v-icon>
        Heatmap
      </v-btn>
    </v-col>

    <!-- Stats -->
    <v-row class="mb-4">
      <v-col cols="12" sm="4">
        <v-card outlined>
          <v-card-text>
            <div class="caption grey--text">Total Submissions</div>
            <div class="headline font-weight-bold">
              {{ submissions.length }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="4">
        <v-card outlined>
          <v-card-text>
            <div class="caption grey--text">Rewarded</div>
            <div class="headline font-weight-bold">{{ rewardedCount }}</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="4">
        <v-card outlined>
          <v-card-text>
            <div class="caption grey--text">Duplicates</div>
            <div class="headline font-weight-bold">{{ duplicateCount }}</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Gallery View -->
    <div v-if="galleryView">
      <div v-if="groupByDate">
        <div
          v-for="(items, date) in groupedSubmissions"
          :key="date"
          class="mb-6"
        >
          <h3 class="subtitle-1 font-weight-bold mb-3">{{ date }}</h3>
          <v-row>
            <v-col
              v-for="item in items"
              :key="item._id"
              cols="12"
              sm="6"
              md="3"
            >
              <v-card outlined>
                <v-img
                  :src="item.imageUrl"
                  aspect-ratio="1.6"
                  class="rounded gallery-image"
                >
                  <v-btn
                    absolute
                    bottom
                    right
                    icon
                    small
                    class="gallery-clock"
                    @click.stop="openInNewTab(item._id)"
                  >
                    <v-icon small color="white">mdi-clock-outline</v-icon>
                  </v-btn>
                </v-img>

                <v-card-text class="py-2">
                  <div class="caption text-truncate">
                    User: {{ item.userId }}
                  </div>
                  <div
                    class="status-pill mb-1"
                    :class="{
                      'pill-green': item.status === 'APPROVED',
                      'pill-amber': !item.status || item.status === 'IN REVIEW',
                      'pill-red': item.status === 'REJECTED',
                    }"
                  >
                    <span class="pill-dot" />
                    {{ item.status || "IN REVIEW" }}
                  </div>
                  <v-chip
                    x-small
                    class="mt-1"
                    :color="item.isDuplicate ? 'red' : 'green'"
                    dark
                  >
                    {{ item.isDuplicate ? "Duplicate" : "Unique" }}
                  </v-chip>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </div>
      </div>

      <v-row v-else>
        <v-col
          v-for="item in filteredSubmissions"
          :key="item._id"
          cols="12"
          sm="6"
          md="3"
        >
          <v-card outlined>
            <v-img
              :src="item.imageUrl"
              aspect-ratio="1.6"
              class="rounded gallery-image"
            >
              <v-btn
                absolute
                bottom
                right
                icon
                small
                class="gallery-clock"
                @click.stop="openInNewTab(item._id)"
              >
                <v-icon small color="white">mdi-clock-outline</v-icon>
              </v-btn>
            </v-img>
            <v-card-text class="py-2">
              <div class="caption text-truncate">User: {{ item.userId }}</div>
              <div class="caption">{{ formatDate(item.createdAt) }}</div>
              <div
                class="status-pill mb-1"
                :class="{
                  'pill-green': item.status === 'APPROVED',
                  'pill-amber': !item.status || item.status === 'IN REVIEW',
                  'pill-red': item.status === 'REJECTED',
                }"
              >
                <span class="pill-dot" />
                {{ item.status || "IN REVIEW" }}
              </div>
              <v-chip
                x-small
                class="mt-1"
                :color="item.isDuplicate ? 'red' : 'green'"
                dark
              >
                {{ item.isDuplicate ? "Duplicate" : "Unique" }}
              </v-chip>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- Table View -->
    <v-card outlined v-else>
      <v-data-table
        :headers="headers"
        :items="filteredSubmissions"
        item-key="_id"
        class="elevation-0"
        dense
      >
        <template v-slot:item.imageUrl="{ item }">
          <v-img
            :src="item.imageUrl"
            max-width="80"
            max-height="60"
            class="rounded"
            aspect-ratio="1.6"
            @click="openPreview(item.imageUrl)"
          />
        </template>

        <template v-slot:item.isRewarded="{ item }">
          <v-chip :color="item.isRewarded ? 'green' : 'orange'" small dark>
            {{ item.isRewarded ? "Yes" : "No" }}
          </v-chip>
        </template>

        <template v-slot:item.status="{ item }">
          <div
            class="status-pill"
            :class="{
              'pill-green': item.status === 'APPROVED',
              'pill-amber': !item.status || item.status === 'IN REVIEW',
              'pill-red': item.status === 'REJECTED',
            }"
          >
            <span class="pill-dot" />
            {{ item.status || "IN REVIEW" }}
          </div>
        </template>

        <template v-slot:item.isDuplicate="{ item }">
          <v-chip :color="item.isDuplicate ? 'red' : 'grey'" small dark>
            {{ item.isDuplicate ? "Duplicate" : "Unique" }}
          </v-chip>
        </template>

        <template v-slot:item.location="{ item }">
          <span class="caption">
            {{ item.location?.coordinates?.[1] }},
            {{ item.location?.coordinates?.[0] }}
          </span>
        </template>

        <template v-slot:item.createdAt="{ item }">
          <span class="caption">{{ formatDate(item.createdAt) }}</span>
        </template>
      </v-data-table>
    </v-card>

    <!-- Image Dialog -->
    <v-dialog v-model="previewDialog" max-width="800">
      <v-card>
        <v-img :src="previewImage" aspect-ratio="1.6" />
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import RestResource from "../../../services/dataServiceMobileV2.js";
const service = new RestResource();

export default {
  data() {
    return {
      galleryView: false,
      groupByDate: false,
      submissions: [],
      search: "",
      previewDialog: false,
      previewImage: null,
      headers: [
        { text: "Preview", value: "imageUrl" },
        { text: "SubmissionId", value: "_id" },
        { text: "User ID", value: "userId" },
        { text: "Status", value: "status" },
        { text: "Location", value: "location" },
        { text: "Created At", value: "createdAt" },
      ],
    };
  },

  computed: {
    groupedSubmissions() {
      return this.filteredSubmissions.reduce((acc, item) => {
        const date = new Date(item.createdAt).toLocaleDateString();
        if (!acc[date]) acc[date] = [];
        acc[date].push(item);
        return acc;
      }, {});
    },

    rewardedCount() {
      return this.submissions.filter((s) => s.status === "APPROVED").length;
    },

    duplicateCount() {
      return this.submissions.filter((s) => s.isDuplicate).length;
    },
    
    filteredSubmissions() {
      if (!this.search) return this.submissions;
      const q = this.search.toLowerCase();
      return this.submissions.filter(
        (s) =>
          s.userId.toLowerCase().includes(q) ||
          s.imageUrl.toLowerCase().includes(q),
      );
    },
  },

  mounted() {
    this.getAllSubmissions();
  },

  methods: {
    async getAllSubmissions() {
      this.$setLoader();
      try {
        const res = await service.getAllOuternetSubmissions();
        this.submissions = res.data.data;
      } finally {
        this.$disableLoader();
      }
    },
    formatDate(date) {
      return new Date(date).toLocaleString();
    },
    openPreview(url) {
      this.previewImage = url;
      this.previewDialog = true;
    },
    openInNewTab(submissionId) {
      if (typeof window !== "undefined") {
        // Adjust the URL to match your route
        window.open(`/mobile/v2/outernet/submission/${submissionId}`, "_blank");
      }
    },
    goToQualityCheck() {
      this.$router.push({ name: "MobileV2OuternetQualityCheck" });
    },
    goToHeatmap() {
      this.$router.push({ name: "MobileV2OuternetHeatmap" });
    },
  },
};
</script>

<style scoped>
.rounded {
  border-radius: 8px;
  cursor: pointer;
}

.gallery-image {
  position: relative;
}

.gallery-clock {
  background: rgba(0, 0, 0, 0.6);
}

/* Status pills */
.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11.5px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 99px;
}

.pill-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.pill-green {
  background: #d1fae5;
  color: #065f46;
}
.pill-green .pill-dot {
  background: #10b981;
}

.pill-red {
  background: #fee2e2;
  color: #991b1b;
}
.pill-red .pill-dot {
  background: #ef4444;
}

.pill-amber {
  background: #fef3c7;
  color: #92400e;
}
.pill-amber .pill-dot {
  background: #f59e0b;
}
</style>
